import { useEffect, useState } from 'react';
import { receiptsAPI } from '@matix/api-client';
import type { ReceiptDetail } from '@matix/types';
import { splitTableLabel } from './helpers';
import { amountText } from '../../store/currency';
import { useLang } from '../../lib/language';

/**
 * Phase 5d.4 — Receipt View modal.
 *
 * Read-only display of a full receipt: header, line items table,
 * payment breakdown, status. Opened from the Receipts screen when
 * the cashier clicks the "View" button on a row.
 *
 * Shape of the modal:
 *   ┌──────────────────────────────────────────────┐
 *   │  Order #466                          [×]     │
 *   ├──────────────────────────────────────────────┤
 *   │  Date: 09/05/2026 14:30                      │
 *   │  Cashier: admin · Drawer #5                  │
 *   │  Customer: —                                 │
 *   │  ─────────────────────────────────────────   │
 *   │  Items                                       │
 *   │  ┌────────────┬──────┬───────┬───────┐       │
 *   │  │ Item       │ Qty  │ Price │ Total │       │
 *   │  │ Pizza      │  2   │ 50.00 │ 100.00│       │
 *   │  │ Soda       │  1   │ 15.00 │  15.00│       │
 *   │  └────────────┴──────┴───────┴───────┘       │
 *   │  ─────────────────────────────────────────   │
 *   │  Subtotal:        115.00                     │
 *   │  Discount:          0.00                     │
 *   │  Tax (15%):        17.25                     │
 *   │  Total:           132.25                     │
 *   │  ─────────────────────────────────────────   │
 *   │  Paid: Cash 132.25                           │
 *   │  ─────────────────────────────────────────   │
 *   │  Status: Open · Editable                     │
 *   └──────────────────────────────────────────────┘
 *
 * Loading flow:
 *   - Mount with receiptId → triggers fetch
 *   - Show spinner while loading
 *   - Show error state on failure with Retry
 *   - Show full detail on success
 *
 * Closing:
 *   - Click [×], the backdrop, or press Escape
 */

export interface ReceiptViewModalProps {
  /** Set to a number to open the modal for that receipt; null to close. */
  receiptId: number | null;
  onClose: () => void;
}

export function ReceiptViewModal({ receiptId, onClose }: ReceiptViewModalProps) {
  const { t } = useLang();
  const [detail, setDetail] = useState<ReceiptDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch details when receiptId changes.
  useEffect(() => {
    if (receiptId === null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);

    receiptsAPI
      .getById(receiptId)
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : t({ en: 'Failed to load receipt', ar: 'تعذّر تحميل الإيصال' });
        setError(message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [receiptId]);

  // Escape key closes the modal.
  useEffect(() => {
    if (receiptId === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [receiptId, onClose]);

  if (receiptId === null) return null;

  return (
    <div
      className="receipt-view-backdrop"
      onClick={(e) => {
        // Click on backdrop (not children) closes.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="receipt-view-modal" role="dialog" aria-modal="true" aria-label={t({ en: 'Receipt details', ar: 'تفاصيل الإيصال' })}>
        <header className="receipt-view-header">
          <h2>{detail ? t({ en: `Order #${detail.entry_no}`, ar: `طلب رقم ${detail.entry_no}` }) : t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</h2>
          <button
            type="button"
            className="receipt-view-close"
            onClick={onClose}
            aria-label={t({ en: 'Close', ar: 'إغلاق' })}
          >
            ×
          </button>
        </header>

        <div className="receipt-view-body">
          {loading && (
            <div className="receipts-state">{t({ en: 'Loading receipt…', ar: 'جارٍ تحميل الإيصال…' })}</div>
          )}

          {error && (
            <div className="receipts-state receipts-state-error">
              <strong>{t({ en: "Couldn't load receipt", ar: 'تعذّر تحميل الإيصال' })}</strong>
              <p>{error}</p>
            </div>
          )}

          {detail && <ReceiptViewBody detail={detail} />}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Body — shown once detail is loaded
// ────────────────────────────────────────────────────────────────────────

function ReceiptViewBody({ detail }: { detail: ReceiptDetail }) {
  const { t } = useLang();
  const voidInfo = detail.void_info ?? null;
  return (
    <>
      {/* ── Voided banner ────────────────────────────────────────────────
          At the TOP, before the numbers. Everything below it — the items,
          the totals, the payment — describes a sale that was cancelled, and
          a reader who meets that fact after the totals has already read them
          as real money.

          `void_info` is null on a receipt the ERP or the legacy till
          cancelled (nothing recorded who did it) and on a tenant that has not
          had migration 060 applied. The banner still appears; it simply names
          nobody, which is the truth rather than a gap. */}
      {detail.is_canceled && (
        <div className="receipt-view-voided" role="status">
          <div className="receipt-view-voided-title">
            {t({ en: 'VOIDED', ar: 'ملغى' })}
          </div>
          {voidInfo ? (
            <dl className="receipt-view-voided-info">
              <div>
                <dt>{t({ en: 'Voided by', ar: 'ألغاه' })}</dt>
                <dd>{voidInfo.voided_by_name ?? `#${voidInfo.voided_by}`}</dd>
              </div>
              <div>
                <dt>{t({ en: 'Voided at', ar: 'وقت الإلغاء' })}</dt>
                <dd>{formatDateTime(voidInfo.voided_at)}</dd>
              </div>
              {voidInfo.reason && (
                <div>
                  <dt>{t({ en: 'Reason', ar: 'السبب' })}</dt>
                  <dd>{voidInfo.reason}</dd>
                </div>
              )}
            </dl>
          ) : (
            <p className="receipt-view-voided-note">
              {t({
                en: 'No record of who cancelled it — this receipt was voided outside the POS.',
                ar: 'لا يوجد سجل لمن ألغاه — تم إلغاء هذا الإيصال خارج نقطة البيع.',
              })}
            </p>
          )}
        </div>
      )}

      {/* ── Header info ──────────────────────────────────────────────── */}
      <section className="receipt-view-section">
        <dl className="receipt-view-info">
          <div>
            <dt>{t({ en: 'Date', ar: 'التاريخ' })}</dt>
            <dd>{formatDateTime(detail.entry_date)}</dd>
          </div>
          <div>
            <dt>{t({ en: 'Cashier', ar: 'الكاشير' })}</dt>
            <dd>
              {detail.empoyee_name ?? '—'}
              {detail.cash_drawer_id !== null ? t({ en: ` · Drawer #${detail.cash_drawer_id}`, ar: ` · درج نقدية رقم ${detail.cash_drawer_id}` }) : ''}
            </dd>
          </div>
          <div>
            <dt>{t({ en: 'Customer', ar: 'العميل' })}</dt>
            <dd>{formatCustomer(detail)}</dd>
          </div>
          {detail.table_label && (
            <div>
              <dt>{splitTableLabel(detail.table_label).term}</dt>
              <dd>{splitTableLabel(detail.table_label).value}</dd>
            </div>
          )}
          {detail.reference_no && (
            <div>
              <dt>{t({ en: 'Reference', ar: 'المرجع' })}</dt>
              <dd>{detail.reference_no}</dd>
            </div>
          )}
          {detail.memo && (
            <div>
              <dt>{t({ en: 'Note', ar: 'ملاحظة' })}</dt>
              <dd>{detail.memo}</dd>
            </div>
          )}
        </dl>
      </section>

      {/* ── Items table ─────────────────────────────────────────────── */}
      <section className="receipt-view-section">
        <h3 className="receipt-view-section-title">{t({ en: 'Items', ar: 'الأصناف' })}</h3>
        {detail.lines.length === 0 ? (
          <p className="receipt-view-empty">{t({ en: 'No line items recorded.', ar: 'لا توجد أصناف مسجّلة.' })}</p>
        ) : (
          <table className="receipt-view-items">
            <thead>
              <tr>
                <th>{t({ en: 'Item', ar: 'الصنف' })}</th>
                <th className="num">{t({ en: 'Qty', ar: 'الكمية' })}</th>
                <th className="num">{t({ en: 'Price', ar: 'السعر' })}</th>
                <th className="num">{t({ en: 'Disc.', ar: 'الخصم' })}</th>
                <th className="num">{t({ en: 'Total', ar: 'الإجمالي' })}</th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((line) => (
                <tr key={line.id} className={line.is_return ? 'is-return' : ''}>
                  <td>
                    {line.item_name}
                    {line.notes && <div className="receipt-view-note">{line.notes}</div>}
                  </td>
                  <td className="num">{formatQty(line.qty)}</td>
                  <td className="num">{formatNumber(line.unit_price)}</td>
                  <td className="num">{formatNumber(line.discount_value)}</td>
                  <td className="num">{formatNumber(line.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Totals breakdown ────────────────────────────────────────── */}
      <section className="receipt-view-section receipt-view-totals">
        <Row label={t({ en: 'Subtotal', ar: 'المجموع الفرعي' })} value={detail.value} />
        {detail.discount_value > 0 && (
          <Row
            label={t({
              en: `Discount${detail.discount_percent > 0 ? ` (${detail.discount_percent}%)` : ''}`,
              ar: `الخصم${detail.discount_percent > 0 ? ` (${detail.discount_percent}%)` : ''}`,
            })}
            value={-detail.discount_value}
          />
        )}
        {detail.delivery_fees > 0 && (
          <Row label={t({ en: 'Delivery', ar: 'التوصيل' })} value={detail.delivery_fees} />
        )}
        {detail.tax_value > 0 && <Row label={t({ en: 'Tax', ar: 'الضريبة' })} value={detail.tax_value} />}
        {detail.service_charge_value > 0 && (
          <Row label={t({ en: 'Service charge', ar: 'رسوم الخدمة' })} value={detail.service_charge_value} />
        )}
        <Row label={t({ en: 'Total', ar: 'الإجمالي' })} value={detail.net_value} bold />
      </section>

      {/* ── Payment breakdown ────────────────────────────────────────── */}
      <section className="receipt-view-section">
        <h3 className="receipt-view-section-title">{t({ en: 'Payment', ar: 'الدفع' })}</h3>
        {detail.payments.length === 0 ? (
          <p className="receipt-view-empty">{t({ en: 'No payments recorded.', ar: 'لا توجد مدفوعات مسجّلة.' })}</p>
        ) : (
          <ul className="receipt-view-payments">
            {detail.payments.map((p, idx) => (
              <li key={`${p.payment_type_id}-${idx}`}>
                <span>
                  {p.payment_type_label}
                  {p.last_four_digits && (
                    <span className="receipt-view-card-tail"> ··· {p.last_four_digits}</span>
                  )}
                </span>
                <span className="num">{formatNumber(p.amount)}</span>
              </li>
            ))}
          </ul>
        )}
        {detail.paid_amount > detail.net_value && (
          <Row label={t({ en: 'Change given', ar: 'الباقي المستحق' })} value={detail.change_amount} subtle />
        )}
      </section>

      {/* ── Status ───────────────────────────────────────────────────── */}
      <section className="receipt-view-section receipt-view-status">
        {detail.is_canceled && (
          <span className="receipts-badge voided">{t({ en: 'Voided', ar: 'ملغى' })}</span>
        )}
        {!detail.is_canceled && detail.is_voucher_posted && (
          <span className="receipts-badge posted">{t({ en: 'Posted (read-only)', ar: 'مُرحّل (للقراءة فقط)' })}</span>
        )}
        {!detail.is_canceled && !detail.is_voucher_posted && (detail.in_open_shift ?? detail.in_current_shift) && (
          <span className="receipts-badge open">{t({ en: 'Open shift', ar: 'وردية مفتوحة' })}</span>
        )}
        {!detail.is_canceled && !detail.is_voucher_posted && !(detail.in_open_shift ?? detail.in_current_shift) && (
          <span className="receipts-badge closed">{t({ en: 'Closed shift', ar: 'وردية مغلقة' })}</span>
        )}
        {!detail.can_edit && detail.edit_blocked_reason && (
          <span className="receipt-view-edit-blocked">
            {t({ en: 'Edit disabled:', ar: 'التعديل معطّل:' })} {detail.edit_blocked_reason}
          </span>
        )}
      </section>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Helpers + sub-components
// ────────────────────────────────────────────────────────────────────────

function Row({
  label,
  value,
  bold,
  subtle,
}: {
  label: string;
  value: number;
  bold?: boolean;
  subtle?: boolean;
}) {
  return (
    <div className={`receipt-view-total-row ${bold ? 'bold' : ''} ${subtle ? 'subtle' : ''}`}>
      <span>{label}</span>
      <span className="num">{formatNumber(value)}</span>
    </div>
  );
}

function formatNumber(n: number): string {
  // Money follows the tenant currency's precision — a 0.136 KWD line printed
  // as "0.14" here while the receipt itself printed 0.136.
  return amountText(n);
}

function formatQty(n: number): string {
  // Keep integer qtys clean; show decimals only when present.
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(3);
}

function formatDateTime(iso: string): string {
  // Naive tenant-TZ string from pos-api — parse components directly so the
  // displayed wall-clock matches the receipt and the cashier screen. Prefix
  // match, not anchored: a trailing 'Z' from an older API build must not send
  // us down the Date.parse path, which would shift the time by the tenant's
  // UTC offset. See the note in ./helpers.ts.
  const naive = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d{1,7})?)?/.exec(iso.trim());
  let year: number, month: number, day: number, hour24: number, minute: number;
  if (naive) {
    year = Number(naive[1]); month = Number(naive[2]); day = Number(naive[3]);
    hour24 = Number(naive[4]); minute = Number(naive[5]);
  } else {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return iso;
    const d = new Date(ms);
    year = d.getFullYear(); month = d.getMonth() + 1; day = d.getDate();
    hour24 = d.getHours(); minute = d.getMinutes();
  }
  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year} ${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${suffix}`;
}

function formatCustomer(detail: ReceiptDetail): string {
  if (detail.customer_name && detail.customer_phone) {
    return `${detail.customer_name} · ${detail.customer_phone}`;
  }
  if (detail.customer_name) return detail.customer_name;
  if (detail.customer_phone) return detail.customer_phone;
  return '—';
}
